import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  AlertTriangle,
  Database,
  Zap,
  Users,
  BookOpen,
  ChevronRight,
  CheckCircle,
  Clock,
  ArrowRight,
} from 'lucide-react';
import { getEscalationRules, getExceptionCodes, getSLACounts } from '../../lib/slaCatalogueRegistry';
import { getAllStages } from '../../lib/leadPipelineRegistry';
import { getGovernanceLinks } from '../../lib/rolesRegistry';

const POLICIES = [
  {
    id: 'data-quality',
    icon: Database,
    color: 'blue',
    titleEn: 'Data Quality & CRM Standards',
    titleAr: 'جودة البيانات ومعايير CRM',
    descEn: 'Mandatory CRM fields, validation rules, data completeness standards, and entry requirements per pipeline stage.',
    descAr: 'حقول CRM الإلزامية وقواعد التحقق ومعايير اكتمال البيانات ومتطلبات الإدخال لكل مرحلة.',
    stats: ['11 Pipeline Stages', '50+ Mandatory Fields', '100% Source Attribution Required'],
    statsAr: ['11 مرحلة', '+50 حقل إلزامي', '100% نسبة تتبع المصدر'],
    badge: 'Critical',
    badgeColor: 'red',
  },
  {
    id: 'escalation',
    icon: AlertTriangle,
    color: 'orange',
    titleEn: 'Escalation Policy',
    titleAr: 'سياسة التصعيد',
    descEn: 'SLA breach escalation chains, auto-reassignment rules, notification channels, and manager intervention thresholds.',
    descAr: 'سلاسل تصعيد خرق الاتفاقية وقواعد إعادة التعيين التلقائية وقنوات الإشعار وعتبات تدخل المدير.',
    stats: ['6 Escalation Rules', '3 Trigger Types', 'Auto-Reassignment on Breach'],
    statsAr: ['6 قواعد تصعيد', '3 أنواع محفزات', 'إعادة تعيين تلقائي عند الخرق'],
    badge: 'High Priority',
    badgeColor: 'orange',
  },
  {
    id: 'exceptions',
    icon: Zap,
    color: 'purple',
    titleEn: 'Exception Handling Policy',
    titleAr: 'سياسة معالجة الاستثناءات',
    descEn: 'SLA exception codes, pause/reset/exempt behaviors, approval workflows, and required fields for each exception type.',
    descAr: 'رموز استثناء الاتفاقية وسلوكيات الإيقاف المؤقت/إعادة التعيين/الإعفاء وسير عمل الموافقة.',
    stats: ['10 Exception Codes', '3 SLA Behaviors', 'Approval Workflows'],
    statsAr: ['10 رموز استثناء', '3 سلوكيات', 'سير عمل الموافقة'],
    badge: 'Operational',
    badgeColor: 'purple',
  },
  {
    id: 'lead-handling',
    icon: Users,
    color: 'green',
    titleEn: 'Lead Handling & Assignment Policy',
    titleAr: 'سياسة معالجة وتعيين العملاء المحتملين',
    descEn: 'Lead ownership rules, assignment logic, cadence compliance, reason-code requirements, and handover protocols.',
    descAr: 'قواعد ملكية العملاء المحتملين ومنطق التعيين والامتثال للجداول الزمنية ومتطلبات رموز الأسباب.',
    stats: ['Single Owner Rule', '8-Attempt Cadence', 'Structured Handover Protocols'],
    statsAr: ['قاعدة مالك واحد', 'جدول 8 محاولات', 'بروتوكولات تسليم منظمة'],
    badge: 'Core',
    badgeColor: 'green',
  },
  {
    id: 'sla-compliance',
    icon: Clock,
    color: 'teal',
    titleEn: 'SLA Compliance Policy',
    titleAr: 'سياسة الامتثال لاتفاقية مستوى الخدمة',
    descEn: 'Channel response time targets, stage duration limits, first-contact SLAs, KPI thresholds, and compliance tracking.',
    descAr: 'أهداف وقت استجابة القناة وحدود مدة المرحلة والاتفاقيات الأولى لمؤشرات الأداء الرئيسية.',
    stats: ['8 Channel SLAs', '10 Stage SLAs', '>95% Compliance Target'],
    statsAr: ['8 اتفاقيات قناة', '10 اتفاقيات مرحلة', '>95% هدف الامتثال'],
    badge: 'Mandatory',
    badgeColor: 'teal',
  },
  {
    id: 'governance',
    icon: BookOpen,
    color: 'slate',
    titleEn: 'Governance & Audit Policy',
    titleAr: 'سياسة الحوكمة والمراجعة',
    descEn: 'Role accountability matrix, RACI framework, audit trails, performance review cadences, and system access governance.',
    descAr: 'مصفوفة مساءلة الأدوار وإطار RACI ومسارات التدقيق وجداول مراجعة الأداء وحوكمة الوصول.',
    stats: ['10 Defined Roles', 'RACI per Stage', 'Full Audit Trail Required'],
    statsAr: ['10 أدوار محددة', 'RACI لكل مرحلة', 'مسار تدقيق كامل مطلوب'],
    badge: 'Framework',
    badgeColor: 'slate',
  },
];

const COLOR_MAP: Record<string, { bg: string; border: string; icon: string; badge: string; text: string }> = {
  blue:   { bg: 'bg-blue-50',   border: 'border-blue-200',   icon: 'text-blue-600',   badge: 'bg-red-100 text-red-700',     text: 'text-blue-700' },
  orange: { bg: 'bg-orange-50', border: 'border-orange-200', icon: 'text-orange-600', badge: 'bg-orange-100 text-orange-700', text: 'text-orange-700' },
  purple: { bg: 'bg-purple-50', border: 'border-purple-200', icon: 'text-purple-600', badge: 'bg-purple-100 text-purple-700', text: 'text-purple-700' },
  green:  { bg: 'bg-green-50',  border: 'border-green-200',  icon: 'text-green-600',  badge: 'bg-green-100 text-green-700',  text: 'text-green-700' },
  teal:   { bg: 'bg-teal-50',   border: 'border-teal-200',   icon: 'text-teal-600',   badge: 'bg-teal-100 text-teal-700',    text: 'text-teal-700' },
  slate:  { bg: 'bg-slate-50',  border: 'border-slate-200',  icon: 'text-slate-600',  badge: 'bg-slate-100 text-slate-700',  text: 'text-slate-700' },
};

export default function PoliciesOverview() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';

  const escalationRules = getEscalationRules();
  const exceptionCodes = getExceptionCodes();
  const slaCounts = getSLACounts();
  const stages = getAllStages();
  const govLinks = getGovernanceLinks();

  const summaryStats = [
    { labelEn: 'Policy Categories', labelAr: 'فئات السياسة', value: POLICIES.length, icon: Shield, color: 'text-blue-600' },
    { labelEn: 'Escalation Rules', labelAr: 'قواعد التصعيد', value: escalationRules.length, icon: AlertTriangle, color: 'text-orange-600' },
    { labelEn: 'Exception Codes', labelAr: 'رموز الاستثناء', value: exceptionCodes.length, icon: Zap, color: 'text-purple-600' },
    { labelEn: 'Stages Covered', labelAr: 'المراحل المغطاة', value: stages.length, icon: CheckCircle, color: 'text-green-600' },
    { labelEn: 'Channel SLAs', labelAr: 'اتفاقيات القنوات', value: slaCounts.channelSLAs, icon: Clock, color: 'text-teal-600' },
    { labelEn: 'Governance Links', labelAr: 'روابط الحوكمة', value: govLinks.length, icon: BookOpen, color: 'text-slate-600' },
  ];

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-3">
          <div className="p-3 bg-blue-600 rounded-xl">
            <Shield className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isAr ? 'السياسات والحوكمة' : 'Policies & Governance'}
            </h1>
            <p className="text-gray-500 text-sm mt-0.5">
              {isAr ? 'الأطر التشغيلية وقواعد الامتثال لنظام إدارة العملاء المحتملين' : 'Operational frameworks and compliance rules for the lead management operating system'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
        {summaryStats.map((stat) => {
          const Icon = stat.icon;
          return (
            <div key={stat.labelEn} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
              <Icon className={`w-5 h-5 ${stat.color} mx-auto mb-2`} />
              <div className="text-2xl font-bold text-gray-900">{stat.value}</div>
              <div className="text-xs text-gray-500 mt-0.5 leading-tight">
                {isAr ? stat.labelAr : stat.labelEn}
              </div>
            </div>
          );
        })}
      </div>

      {/* Policy Cards Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 mb-12">
        {POLICIES.map((policy) => {
          const Icon = policy.icon;
          const colors = COLOR_MAP[policy.color];
          return (
            <Link
              key={policy.id}
              to={`/policies/${policy.id}`}
              className={`group block rounded-2xl border-2 ${colors.border} ${colors.bg} p-6 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5`}
            >
              {/* Card Header */}
              <div className="flex items-start justify-between mb-4">
                <div className={`p-2.5 bg-white rounded-xl shadow-sm border ${colors.border}`}>
                  <Icon className={`w-6 h-6 ${colors.icon}`} />
                </div>
                <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${colors.badge}`}>
                  {policy.badge}
                </span>
              </div>

              {/* Title */}
              <h2 className={`text-lg font-bold mb-2 ${colors.text} group-hover:underline`}>
                {isAr ? policy.titleAr : policy.titleEn}
              </h2>

              {/* Description */}
              <p className="text-sm text-gray-600 mb-4 leading-relaxed">
                {isAr ? policy.descAr : policy.descEn}
              </p>

              {/* Stats */}
              <div className="space-y-1.5 mb-5">
                {(isAr ? policy.statsAr : policy.stats).map((s, i) => (
                  <div key={i} className="flex items-center gap-2 text-xs text-gray-600">
                    <CheckCircle className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                    <span>{s}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className={`flex items-center gap-1.5 text-sm font-semibold ${colors.text}`}>
                {isAr ? 'عرض السياسة' : 'View Policy'}
                <ChevronRight className={`w-4 h-4 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180' : ''}`} />
              </div>
            </Link>
          );
        })}
      </div>

      {/* Governance Links Section */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-8">
        <h2 className="text-xl font-bold text-gray-900 mb-1">
          {isAr ? 'روابط الحوكمة' : 'Governance Cross-References'}
        </h2>
        <p className="text-sm text-gray-500 mb-5">
          {isAr ? 'وحدات النظام المرتبطة بالسياسات والامتثال' : 'System modules linked to policies and compliance'}
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {govLinks.map((link) => (
            <Link
              key={link.path ?? link.module}
              to={link.path ?? '/'}
              className="flex items-center gap-3 p-4 rounded-xl border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all group"
            >
              <ArrowRight className="w-4 h-4 text-blue-500 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
              <div>
                <div className="text-sm font-semibold text-gray-800">
                  {isAr ? link.moduleAr : link.module}
                </div>
                <div className="text-xs text-gray-500 leading-tight mt-0.5">
                  {isAr ? link.descriptionAr : link.description}
                </div>
              </div>
            </Link>
          ))}
        </div>
      </div>

      {/* Compliance Principles */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-6 text-white">
        <h2 className="text-xl font-bold mb-4">
          {isAr ? 'مبادئ الامتثال الأساسية' : 'Core Compliance Principles'}
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { en: 'Single Owner at Every Stage', ar: 'مالك واحد في كل مرحلة', icon: '👤' },
            { en: 'Every Lead Has a Next Action', ar: 'كل عميل له إجراء تالٍ', icon: '✅' },
            { en: 'Data Captured at Point of Entry', ar: 'البيانات تُسجل عند الإدخال', icon: '📊' },
            { en: 'SLA Breach Triggers Auto-Escalation', ar: 'خرق الاتفاقية يُفعّل التصعيد التلقائي', icon: '⚡' },
          ].map((p) => (
            <div key={p.en} className="bg-white/10 rounded-xl p-4 backdrop-blur-sm">
              <div className="text-2xl mb-2">{p.icon}</div>
              <div className="text-sm font-medium leading-tight">
                {isAr ? p.ar : p.en}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
